import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

const getPageScrollTop = () => Math.max(
  window.scrollY || window.pageYOffset || 0,
  document.scrollingElement?.scrollTop || 0,
  document.documentElement?.scrollTop || 0,
  document.body?.scrollTop || 0,
);

const hasOpenModal = () => Array.from(document.querySelectorAll(
  '[role="dialog"], [role="alertdialog"], dialog[open]',
)).some((element) => {
  if (element.closest('[hidden], [aria-hidden="true"]') || element.dataset.state === 'closed') {
    return false;
  }
  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
});

export default function BackToTop({ blocked = false, label, routeKey = '' }) {
  const [visibility, setVisibility] = useState({ routeKey, visible: false });

  useEffect(() => {
    let frame = null;
    const syncVisibility = () => {
      frame = null;
      const threshold = Math.max(480, Math.round((window.innerHeight || 0) * 0.9));
      const visible = getPageScrollTop() > threshold && !hasOpenModal();
      setVisibility((current) => current.routeKey === routeKey && current.visible === visible
        ? current : { routeKey, visible });
    };
    const scheduleSync = () => {
      if (frame !== null) return;
      if (typeof window.requestAnimationFrame === 'function') {
        frame = window.requestAnimationFrame(syncVisibility);
      } else {
        syncVisibility();
      }
    };

    syncVisibility();
    window.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('resize', scheduleSync);
    const observer = new MutationObserver(scheduleSync);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal', 'aria-hidden', 'role', 'hidden', 'open', 'data-state'],
    });

    return () => {
      window.removeEventListener('scroll', scheduleSync);
      window.removeEventListener('resize', scheduleSync);
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame?.(frame);
      frame = null;
    };
  }, [routeKey]);

  const handleClick = (event) => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    if (event.detail === 0) {
      document.getElementById('app-main-content')?.focus({ preventScroll: true });
    }
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: reduceMotion || window.innerWidth < 1024 ? 'auto' : 'smooth',
    });
  };

  if (blocked || !visibility.visible || visibility.routeKey !== routeKey) return null;

  return createPortal(
    <Button
      aria-label={label}
      aria-controls="app-main-content"
      className="fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] right-[calc(1rem+env(safe-area-inset-right))] z-30 h-11 w-11 rounded-full border border-white/10 bg-slate-950/85 text-white shadow-[0_18px_45px_rgba(15,23,42,0.35)] backdrop-blur-md transition-colors duration-200 hover:bg-slate-900 md:bottom-[calc(2rem+env(safe-area-inset-bottom))] md:right-[calc(1.5rem+env(safe-area-inset-right))]"
      onClick={handleClick}
      size="icon"
      style={{ touchAction: 'manipulation' }}
      title={label}
      type="button"
      variant="secondary"
    >
      <ArrowUp aria-hidden="true" className="h-4 w-4" />
    </Button>,
    document.body,
  );
}
